// A note: Markdown on the left, the rendered note on the right. [[ starts a link to an entity,
// a sketch, a migration or another note; links in the preview open what they point at.
import { autocompletion, type Completion, type CompletionContext } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { markdown } from '@codemirror/lang-markdown';
import { syntaxHighlighting } from '@codemirror/language';
import { EditorState } from '@codemirror/state';
import { drawSelection, EditorView, keymap } from '@codemirror/view';
import type { DockviewPanelApi } from 'dockview-react';
import { Copy, FileCode2, FolderOpen, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { editorTheme, highlight } from '../lib/editorTheme';
import { PHONE, useMedia } from '../lib/media';
import { TextInput } from '../sketch/fields';
import { closePanel, openNote, openSourceFile } from '../state/dock';
import { deleteNote, duplicateNote, setActiveNote, updateNote } from '../state/notes';
import { useWorkspace } from '../state/workspace';
import { LinkPreview } from './LinkPreview';
import { resolveLink, type LinkIndex, type LinkTarget } from '@modelviewer/core';
import { renderMarkdown } from './markdown';
import { createLinkedNote, openTarget } from './navigate';
import { useLinkIndex } from './useLinkIndex';

type Mode = 'edit' | 'split' | 'preview';

/** What [[ can complete to: notes, sketches, entities (qualified when a name is in several contexts), migrations. */
export function linkCompletions(index: LinkIndex): Completion[] {
  const out: Completion[] = [];
  for (const n of index.notes) out.push({ label: n.title, type: 'note', detail: 'note' });
  for (const s of index.sketches) out.push({ label: `sketch:${s.title}`, displayLabel: s.title, type: 'sketch', detail: 'sketch' });
  const counts = new Map<string, number>();
  for (const m of index.models) if (m.kind === 'context') for (const e of Object.keys(m.entities)) counts.set(e, (counts.get(e) ?? 0) + 1);
  for (const m of index.models) {
    for (const [e, props] of Object.entries(m.entities)) {
      const name = m.kind === 'context' && counts.get(e) === 1 ? e : `${m.name}/${e}`;
      out.push({ label: name, type: 'entity', detail: m.name });
      for (const p of props) out.push({ label: `${name}.${p}`, type: 'property', detail: m.name, boost: -10 });
    }
  }
  for (const m of index.migrations) out.push({ label: `migration:${m.name}`, displayLabel: m.name, type: 'migration', detail: `${m.context} migration`, boost: -5 });
  return out;
}

function Editor({ noteId, body, index }: { noteId: string; body: string; index: LinkIndex }) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const completions = useRef<Completion[]>([]);
  completions.current = useMemo(() => linkCompletions(index), [index]);

  useEffect(() => {
    const complete = (context: CompletionContext) => {
      const m = context.matchBefore(/\[\[[^\]\n|]*/);
      if (!m) return null;
      const closed = context.state.sliceDoc(context.pos, context.pos + 2) === ']]';
      return {
        from: m.from + 2,
        options: completions.current.map((c) => ({ ...c, apply: closed ? c.label : `${c.label}]]` })),
        validFor: /^[^\]\n|]*$/,
      };
    };
    view.current = new EditorView({
      parent: host.current!,
      state: EditorState.create({
        doc: body,
        extensions: [
          history(),
          drawSelection(),
          markdown(),
          EditorView.lineWrapping,
          autocompletion({ override: [complete], icons: false }),
          keymap.of([...defaultKeymap, ...historyKeymap]),
          syntaxHighlighting(highlight),
          editorTheme,
          EditorView.theme({ '.cm-content': { fontFamily: 'var(--font-sans)', fontSize: '13.5px', lineHeight: '1.6', padding: '16px 0' }, '.cm-line': { padding: '0 20px' } }),
          EditorView.contentAttributes.of({ 'aria-label': 'Note text' }),
          EditorView.updateListener.of((u) => {
            if (u.docChanged) updateNote(noteId, { body: u.state.doc.toString() });
          }),
        ],
      }),
    });
    return () => {
      view.current?.destroy();
      view.current = null;
    };
    // The editor owns the text after it starts; outside changes are applied below.
  }, [noteId]);

  useEffect(() => {
    const v = view.current;
    if (v && v.state.doc.toString() !== body && !v.hasFocus) v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: body } });
  }, [body]);

  return <div ref={host} className="h-full min-w-0 overflow-hidden [&_.cm-editor]:h-full" data-testid="note-editor" />;
}

function Preview({ body, index }: { body: string; index: LinkIndex }) {
  const html = useMemo(() => renderMarkdown(body, (t) => resolveLink(t, index)), [body, index]);
  const [hover, setHover] = useState<{ target: LinkTarget | 'unresolved'; x: number; y: number } | null>(null);
  const linkAt = (el: EventTarget) => (el instanceof Element ? (el.closest('a.mv-wikilink') as HTMLAnchorElement | null) : null);
  return (
    <div
      className="mv-prose h-full min-w-0 overflow-y-auto px-6 py-5"
      data-testid="note-preview"
      onClick={(e) => {
        const a = linkAt(e.target);
        if (!a) return;
        e.preventDefault();
        const target = a.dataset['target']!;
        const resolved = resolveLink(target, index);
        setHover(null);
        if (resolved) openTarget(resolved);
        else void createLinkedNote(target);
      }}
      onMouseOver={(e) => {
        const a = linkAt(e.target);
        if (!a) return;
        const rect = a.getBoundingClientRect();
        setHover({ target: resolveLink(a.dataset['target']!, index) ?? 'unresolved', x: rect.left, y: rect.top });
      }}
      onMouseOut={(e) => linkAt(e.target) && setHover(null)}
    >
      {body.trim() ? <div dangerouslySetInnerHTML={{ __html: html }} /> : <p className="text-faint">Nothing written yet.</p>}
      {hover && <LinkPreview target={hover.target} x={hover.x} y={hover.y} />}
    </div>
  );
}

export function NoteView({ noteId, api }: { noteId: string; api: DockviewPanelApi }) {
  const note = useWorkspace((s) => s.notes[noteId]);
  const index = useLinkIndex();
  const [chosen, setMode] = useState<Mode | null>(null);
  // A phone has no room for both side by side: it shows one, the text to read if there is any.
  const phone = useMedia(PHONE);
  const [phoneMode] = useState<Mode>(() => (note?.body.trim() ? 'preview' : 'edit'));
  const mode: Mode = chosen && !(phone && chosen === 'split') ? chosen : phone ? phoneMode : 'split';

  useEffect(() => {
    const d = api.onDidActiveChange((e) => e.isActive && setActiveNote(noteId));
    if (api.isActive) setActiveNote(noteId);
    return () => {
      d.dispose();
      // Closed or hidden: the inspector should not keep showing it.
      if (useWorkspace.getState().activeNote === noteId) setActiveNote(null);
    };
  }, [api, noteId]);
  const title = note?.title;
  useEffect(() => {
    if (title) api.setTitle(title);
  }, [api, title]);

  if (!note) return <div className="flex h-full items-center justify-center text-muted">This note was deleted.</div>;
  if (note.origin) {
    // A file in the folder: shown as it is, updated when the file changes.
    return (
      <div className="flex h-full flex-col" data-testid="note">
        <div className="flex flex-none items-center gap-2 border-b border-border bg-panel px-3 py-1.5 text-[12px] text-muted" role="note">
          <FolderOpen size={13} className="flex-none text-accent" />
          <span className="min-w-0 truncate">
            A file in the folder: <code className="font-mono text-text">{note.origin.path}</code>
          </span>
          <button onClick={() => openSourceFile(note.origin!.path)} className="ml-auto flex flex-none items-center gap-1 rounded px-1.5 py-0.5 text-accent hover:bg-hover">
            <FileCode2 size={12} /> Open file
          </button>
          <button
            onClick={() =>
              void duplicateNote(noteId).then((copy) => {
                if (copy) openNote(copy.id);
              })
            }
            className="flex flex-none items-center gap-1 rounded px-1.5 py-0.5 text-accent hover:bg-hover"
          >
            <Copy size={12} /> Duplicate to edit
          </button>
        </div>
        <div className="min-h-0 flex-1">
          <Preview body={note.body} index={index} />
        </div>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col" data-testid="note">
      <div className="flex h-11 flex-none items-center gap-2 border-b border-border px-3">
        <div className="min-w-0 flex-1">
          <TextInput value={note.title} label="Note title" variant="inline" size="lg" onCommit={(title) => (title ? updateNote(noteId, { title }) : false)} />
        </div>
        <div className="flex rounded-md bg-panel-2 p-0.5" role="radiogroup" aria-label="Note view">
          {(
            [
              ['edit', 'Edit'],
              ['split', 'Split'],
              ['preview', 'Read'],
            ] as const
          )
            .filter(([value]) => !(phone && value === 'split'))
            .map(([value, label]) => (
              <button
                key={value}
                role="radio"
                aria-checked={mode === value}
                onClick={() => setMode(value)}
                className={cn('h-6 rounded px-2 text-[12px] pointer-coarse:h-8 pointer-coarse:px-3', mode === value ? 'bg-active text-text' : 'text-muted hover:text-text')}
              >
                {label}
              </button>
            ))}
        </div>
        <button
          title="Delete this note"
          aria-label="Delete this note"
          onClick={() => {
            if (!window.confirm(`Delete the note "${note.title}"? This cannot be undone.`)) return;
            closePanel(`note:${noteId}`);
            void deleteNote(noteId);
          }}
          className="flex h-7 w-7 items-center justify-center rounded-md text-faint hover:bg-removed-soft hover:text-removed"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <div className="flex min-h-0 flex-1">
        {mode !== 'preview' && (
          <div className={cn('min-w-0', mode === 'split' ? 'w-1/2 border-r border-border' : 'flex-1')}>
            <Editor noteId={noteId} body={note.body} index={index} />
          </div>
        )}
        {mode !== 'edit' && (
          <div className="min-w-0 flex-1">
            <Preview body={note.body} index={index} />
          </div>
        )}
      </div>
    </div>
  );
}
